import Workspace, {
  WorkspaceDocument,
  WorkspaceDocumentRevision,
  WorkspaceDocumentType,
  WorkspaceFolder,
  WorkspaceLink,
} from "@/models/workspace/Workspace";
import { WorkspaceDocumentMetadata } from "@/models/metadata/metadata";
import { WorkspaceSearch } from "@/pages/workspace/providers/WorkspaceProvider";
import { UploadSource } from "@/components/upload/types/upload-item";
import { INDEXING_EXPECTATION } from "@/pages/workspace/utils/indexingProgress";

// Re-export from shared-ui so existing callers don't need to change imports.
export { isWorkspaceDocumentHistoric } from "@capacit-com/lara-law-shared-ui";

// ---------------------------------------------------------------------------

/** Item type value for folder. Use when checking item.type. */
export const ITEM_TYPE_FOLDER = "folder" as const;
/** Item type value for document. Use when checking item.type. */
export const ITEM_TYPE_DOCUMENT = "document" as const;
/** Item type value for a web link (LAR-415). Use when checking item.type. */
export const ITEM_TYPE_LINK = "link" as const;

export type ItemType =
  typeof ITEM_TYPE_FOLDER | typeof ITEM_TYPE_DOCUMENT | typeof ITEM_TYPE_LINK;

/** The Danish noun for each item type, for dialog titles ("Slet mappe") and toasts ("Kunne ikke slette mappen"). */
export const ITEM_TYPE_LABELS: Record<
  ItemType,
  { indefinite: string; definite: string }
> = {
  [ITEM_TYPE_FOLDER]: { indefinite: "mappe", definite: "mappen" },
  [ITEM_TYPE_DOCUMENT]: { indefinite: "dokument", definite: "dokumentet" },
  [ITEM_TYPE_LINK]: { indefinite: "link", definite: "linket" },
};

export type Item = {
  id: string;
  type: ItemType;
  name: string;
  /** Only set for links: the absolute http(s) address the row opens. */
  url?: string;
  createdAt?: string;
  updatedAt?: string;
  workspaceId?: string;
  size?: number;
  fileCount?: number;
  documentType?: WorkspaceDocumentType;
  documentSource?: UploadSource;
  userId: string;
  blobName: string;
  driveId?: string;
  siteId?: string;
  sharepointFileId?: string;
  subscribers?: string[];
  revisions?: WorkspaceDocumentRevision[];
  activeRevision?: number;
  documentMetadataList?: WorkspaceDocumentMetadata[];
  filePath?: string;
};

// Simple helper to build filepath from current path
const buildFilePath = (
  currentPath: { id: string; name: string }[],
  itemName: string
): string => {
  const pathNames = currentPath.map((p) => p.name);
  return pathNames.length > 0 ? `${pathNames.join("/")}/${itemName}` : itemName;
};

/**
 * Recursively calculates the newest updatedAt date from a folder and all its children.
 * Checks the folder's own updatedAt, all document updatedAt values, and recursively
 * checks all nested folders.
 *
 * Pass the same `cache` for all calls while flattening a workspace so each folder
 * is traversed once (e.g. from {@link getAllWorkspaceItems}).
 */
const getNewestDateFromFolder = (
  folder: WorkspaceFolder,
  cache: WeakMap<WorkspaceFolder, string>
): string => {
  const cached = cache.get(folder);
  if (cached !== undefined) {
    return cached;
  }

  let newestStr = folder.updatedAt || folder.createdAt || "";
  let newestMs = newestStr ? Date.parse(newestStr) : NaN;

  folder.documents.forEach((doc) => {
    const docDate = doc.updatedAt || doc.createdAt || "";
    if (!docDate) return;
    const ms = Date.parse(docDate);
    if (!Number.isFinite(newestMs) || ms > newestMs) {
      newestMs = ms;
      newestStr = docDate;
    }
  });

  (folder.links ?? []).forEach((link) => {
    const linkDate = link.updatedAt || link.createdAt || "";
    if (!linkDate) return;
    const ms = Date.parse(linkDate);
    if (!Number.isFinite(newestMs) || ms > newestMs) {
      newestMs = ms;
      newestStr = linkDate;
    }
  });

  folder.folders.forEach((subfolder) => {
    const subStr = getNewestDateFromFolder(subfolder, cache);
    if (!subStr) return;
    const ms = Date.parse(subStr);
    if (!Number.isFinite(newestMs) || ms > newestMs) {
      newestMs = ms;
      newestStr = subStr;
    }
  });

  cache.set(folder, newestStr);
  return newestStr;
};

// Function to get only root-level items (folders and documents at workspace root)
export function getRootLevelItems(workspace: Workspace): Item[] {
  const items: Item[] = [];
  const folderDateCache = new WeakMap<WorkspaceFolder, string>();

  // Add root-level folders
  workspace.folders.forEach((folder) => {
    items.push(workspaceFolderToItem(folder, [], true, folderDateCache));
  });

  // Add root-level documents
  workspace.documents.forEach((doc) => {
    items.push(workspaceDocumentToItem(doc, [], true));
  });

  // Add root-level links
  (workspace.links ?? []).forEach((link) => {
    items.push(workspaceLinkToItem(link, [], true));
  });

  return items;
}

// Function to get items within a specific folder
export function getFolderItems(
  folder: WorkspaceFolder,
  currentPath: { id: string; name: string }[] = []
): Item[] {
  const items: Item[] = [];
  const folderDateCache = new WeakMap<WorkspaceFolder, string>();

  folder.folders.forEach((subfolder) => {
    items.push(
      workspaceFolderToItem(subfolder, currentPath, false, folderDateCache)
    );
  });

  folder.documents.forEach((doc) => {
    items.push(workspaceDocumentToItem(doc, currentPath));
  });

  (folder.links ?? []).forEach((link) => {
    items.push(workspaceLinkToItem(link, currentPath));
  });

  return items;
}

/**
 * Document types that participate in chat / kildesøgning indexing (kildeklargøring).
 * Wider upload/preview sets are separate — see {@link WORKSPACE_PREVIEWABLE_IN_DOCUMENT_VIEWER}.
 */
export const WORKSPACE_CHAT_INDEXABLE_DOCUMENT_TYPES = [
  WorkspaceDocumentType.PDF,
  WorkspaceDocumentType.DOC,
  WorkspaceDocumentType.DOCX,
] as const satisfies readonly WorkspaceDocumentType[];

/**
 * Types that can be opened in the workspace embedded viewer (Word, PDF, Excel,
 * PowerPoint, plus TXT/MD where the viewer supports them).
 * Not to be used for chat-indexing eligibility.
 */
export const WORKSPACE_PREVIEWABLE_IN_DOCUMENT_VIEWER: WorkspaceDocumentType[] =
  [
    WorkspaceDocumentType.DOC,
    WorkspaceDocumentType.DOCX,
    WorkspaceDocumentType.PDF,
    WorkspaceDocumentType.XLS,
    WorkspaceDocumentType.XLSX,
    WorkspaceDocumentType.XLSM,
    WorkspaceDocumentType.PPT,
    WorkspaceDocumentType.PPTX,
    WorkspaceDocumentType.TXT,
    WorkspaceDocumentType.MD,
  ];

export const isChatIndexableDocumentType = (
  documentType?: WorkspaceDocumentType
): boolean =>
  documentType !== undefined &&
  (
    WORKSPACE_CHAT_INDEXABLE_DOCUMENT_TYPES as readonly WorkspaceDocumentType[]
  ).includes(documentType);

/**
 * Whether this item's content is kept in step with a file in OneDrive/SharePoint.
 *
 * An item with no recorded source counts as local: the API defaults
 * `DocumentSource` to `Local`, so a row saved before the field existed is not
 * cloud-linked.
 */
export const isCloudLinkedItem = (item: Item): boolean =>
  item.documentSource === UploadSource.ONEDRIVE ||
  item.documentSource === UploadSource.SHAREPOINT;

/** True when the type can be shown in {@link EmbedPDFViewer} / workspace document view. */
export const isWorkspaceDocumentPreviewableInViewer = (
  documentType: WorkspaceDocumentType
): boolean => WORKSPACE_PREVIEWABLE_IN_DOCUMENT_VIEWER.includes(documentType);

/**
 * Word / PDF / Excel / PowerPoint only. Used for Status tooltips that mention
 * opening the file in the app — avoid for TXT/MD/OTHER so misclassified types
 * (e.g. CSS/XML stored as TXT) do not promise an in-app viewer.
 */
const WORKSPACE_OFFICE_FAMILY_FOR_INDEXING_STATUS_TOOLTIP: WorkspaceDocumentType[] =
  [
    WorkspaceDocumentType.DOC,
    WorkspaceDocumentType.DOCX,
    WorkspaceDocumentType.PDF,
    WorkspaceDocumentType.XLS,
    WorkspaceDocumentType.XLSX,
    WorkspaceDocumentType.XLSM,
    WorkspaceDocumentType.PPT,
    WorkspaceDocumentType.PPTX,
  ];

const isOfficeFamilyForIndexingStatusTooltip = (
  documentType?: WorkspaceDocumentType
): boolean =>
  documentType !== undefined &&
  WORKSPACE_OFFICE_FAMILY_FOR_INDEXING_STATUS_TOOLTIP.includes(documentType);

export type WorkspaceFileIndexingStatusVariant =
  | "indexed"
  | "notApplicable"
  /** Indexed before the current pipeline version: still usable, but search may miss it (LAR-235). */
  | "stale"
  | "actionNeeded"
  | "indexing";

/**
 * Why a document carries the amber marker: its search index predates the current
 * pipeline version. One sentence for every surface — files tab, Workspaces page search
 * and global search — so the marker reads the same wherever it appears.
 */
export const STALE_SEARCH_INDEX_TOOLTIP =
  "Søgeindekset for dokumentet er fra før en platformopgradering. Dokumentet kan stadig åbnes og bruges, men det kan mangle i søgninger, og antallet af matches kan være for lavt. Genkør kildeklargøring for at opdatere det.";

export type WorkspaceFileIndexingStatusPresentation = {
  variant: WorkspaceFileIndexingStatusVariant;
  tooltip: string;
};

/** Minimal shape for active-revision stale detection (Item, WorkspaceDocument, etc.). */
export type ActiveRevisionSearchIndexStaleSource = {
  revisions?: WorkspaceDocumentRevision[];
  activeRevision?: number;
};

/** Active revision needs re-index after pipeline version drift (see SearchIndex staleness). */
export const isActiveRevisionSearchIndexStale = (
  document: ActiveRevisionSearchIndexStaleSource
): boolean => {
  const activeRevision = document.revisions?.find(
    (revision) => revision.revisionVersion === document.activeRevision
  );
  return activeRevision?.searchIndexStale === true;
};

/**
 * Drive Status column icon/tooltip: chat indexing outcome vs types that are never indexed for chat.
 */
export const getWorkspaceFileIndexingStatusPresentation = (params: {
  isIndexed: boolean;
  searchIndexStale?: boolean;
  documentType?: WorkspaceDocumentType;
  /** An indexing run is in flight for this document (LAR-252 async indexing). */
  isIndexing?: boolean;
}): WorkspaceFileIndexingStatusPresentation => {
  const { isIndexed, searchIndexStale, documentType, isIndexing } = params;

  const successTooltip =
    "Dette dokument er klar til brug og kan bruges i kildesøgning.";

  if (isChatIndexableDocumentType(documentType)) {
    if (isIndexing) {
      return {
        variant: "indexing",
        tooltip: `Kildeklargøring kører. ${INDEXING_EXPECTATION}`,
      };
    }

    if (searchIndexStale) {
      return { variant: "stale", tooltip: STALE_SEARCH_INDEX_TOOLTIP };
    }

    if (isIndexed) {
      return { variant: "indexed", tooltip: successTooltip };
    }

    return {
      variant: "actionNeeded",
      tooltip:
        "Dette dokument er ikke klar til brug — genkør venligst kildeklargøring for at kunne bruge det i kildesøgninger.",
    };
  }

  if (isOfficeFamilyForIndexingStatusTooltip(documentType)) {
    return {
      variant: "notApplicable",
      tooltip:
        "Denne filtype indekseres ikke til kildesøgning (kun PDF og Word). Du kan stadig åbne og vise filen i appen.",
    };
  }

  return {
    variant: "notApplicable",
    tooltip:
      "Denne filtype indekseres ikke til kildesøgning. Dokumentet kan stadig være tilgængeligt i arbejdsområdet.",
  };
};

/**
 * Eligible for chat/kildesøgning indexing (kildeklargøring / reindex), not merely preview.
 */
export const isItemIndexable = (
  item?: Item,
  documentType?: WorkspaceDocumentType
) => {
  if (documentType !== undefined) {
    return isChatIndexableDocumentType(documentType);
  }
  if (item?.documentType !== undefined) {
    return isChatIndexableDocumentType(item.documentType);
  }
  return false;
};

/**
 * Finds an item (folder or document) by id in the workspace.
 * @param workspace - The workspace to search in
 * @param id - The id of the item to find
 * @returns The item or null if not found
 */
export function getItemById(workspace: Workspace, id: string): Item | null {
  const items = getAllWorkspaceItems(workspace);
  return items.find((i) => i.id === id) ?? null;
}

// Function to get all workspace items with filepaths (for search functionality)
export function getAllWorkspaceItems(workspace: Workspace): Item[] {
  const items: Item[] = [];
  const folderDateCache = new WeakMap<WorkspaceFolder, string>();

  // Add root documents
  workspace.documents?.forEach((doc) => {
    items.push(workspaceDocumentToItem(doc, [], true));
  });

  // Add root links
  workspace.links?.forEach((link) => {
    items.push(workspaceLinkToItem(link, [], true));
  });

  // Add root folders and their contents recursively
  const addFolderContents = (
    folder: WorkspaceFolder,
    currentPath: { id: string; name: string }[] = []
  ) => {
    // Build the new path including this folder
    const newPath = [...currentPath, { id: folder.id, name: folder.name }];

    // Add the folder itself as an item
    items.push(
      workspaceFolderToItem(folder, currentPath, false, folderDateCache)
    );

    // Add all documents inside this folder
    folder.documents?.forEach((doc: WorkspaceDocument) => {
      items.push(workspaceDocumentToItem(doc, newPath, false));
    });

    // Add all links inside this folder
    folder.links?.forEach((link: WorkspaceLink) => {
      items.push(workspaceLinkToItem(link, newPath, false));
    });

    // Recursively handle subfolders
    folder.folders?.forEach((subFolder: WorkspaceFolder) => {
      addFolderContents(subFolder, newPath);
    });
  };

  workspace.folders?.forEach((folder) => {
    addFolderContents(folder);
  });

  return items;
}

/**
 * Case- and composition-insensitive form for matching: file names from macOS and
 * SharePoint are decomposed (NFD, "å" = "a" + U+030A) while a keyboard types NFC, so a
 * plain `includes` never found "rådighed" in them.
 */
const searchable = (value: string): string =>
  value.normalize("NFC").toLowerCase();

/** What the files tab shows for a document the search index matched on its text. */
export interface TextMatch {
  /** Approximate occurrences of the search words in the text; 0 when the index kept no positions. */
  contentMatchCount: number;
  searchIndexStale: boolean;
}

/**
 * The files tab search. Names (and link addresses, and metadata when asked) match as
 * typed; `textMatchIds` are documents the search index found by their text (LAR-235),
 * admitted too, but only from `items`, so a folder scope still holds.
 */
export const filterItemsForSearch = (
  items: Item[],
  search: WorkspaceSearch,
  textMatchIds?: ReadonlySet<string>
) => {
  const searchLower = searchable(search.searchString);
  return items.filter(
    (item) =>
      (item.type === ITEM_TYPE_DOCUMENT && textMatchIds?.has(item.id)) ||
      searchable(item.name).includes(searchLower) ||
      (item.type === ITEM_TYPE_LINK &&
        item.url?.toLowerCase().includes(searchLower)) ||
      (search.searchOnMetadata &&
        item.documentMetadataList?.some(
          (documentMetadata: WorkspaceDocumentMetadata) =>
            searchable(documentMetadata.metadata.stringValue ?? "").includes(
              searchLower
            ) ||
            documentMetadata.metadata.numberValue
              ?.toString()
              .toLowerCase()
              .includes(searchLower) ||
            documentMetadata.metadata.booleanValue
              ?.toString()
              .toLowerCase()
              .includes(searchLower) ||
            documentMetadata.metadata.dateTimeValues?.some((dateTime) => {
              // Convert ISO string to DD/MM/YYYY format
              const date = new Date(dateTime);
              const day = date.getDate().toString().padStart(2, "0");
              const month = (date.getMonth() + 1).toString().padStart(2, "0");
              const year = date.getFullYear();
              const formattedDate = `${day}/${month}/${year}`;

              return formattedDate.toLowerCase().includes(searchLower);
            }) ||
            documentMetadata.metadata.selectValues?.some((selectValue) =>
              selectValue.toLowerCase().includes(searchLower)
            )
        ))
  );
};

/**
 * The documents among `items` that are in the search results only because of their text,
 * so their rows can say why ("Fandt 12 matches"), as the Workspaces search does.
 */
export const pickTextOnlyMatches = (
  items: Item[],
  search: WorkspaceSearch,
  textMatches: ReadonlyMap<string, TextMatch>
): Map<string, TextMatch> => {
  const textOnly = new Map<string, TextMatch>();
  if (textMatches.size === 0) return textOnly;
  for (const item of items) {
    const match = textMatches.get(item.id);
    if (!match || item.type !== ITEM_TYPE_DOCUMENT) continue;
    if (filterItemsForSearch([item], search).length === 0) {
      textOnly.set(item.id, match);
    }
  }
  return textOnly;
};

// Group search results by folder path for better organization
export const groupSearchResultsByPath = (items: Item[], sortBy: string) => {
  const groupedResults: { [path: string]: Item[] } = {};
  items.forEach((item) => {
    // Get the parent folder path, not the full path including the item name
    let parentPath = "root";

    if (item.filePath) {
      // Extract the parent folder path by removing the item name from the end
      const pathParts = item.filePath.split("/");
      if (pathParts.length > 1) {
        // Remove the last part (item name) to get the parent folder path
        parentPath = pathParts.slice(0, -1).join("/");
      } else {
        // If there's only one part, it means the item is in root
        parentPath = "root";
      }
    }

    if (!groupedResults[parentPath]) {
      groupedResults[parentPath] = [];
    }

    groupedResults[parentPath].push(item);
  });

  // Sort items within each group by name
  Object.keys(groupedResults).forEach((path) => {
    groupedResults[path].sort((a, b) => {
      return sortItemsBy(a, b, sortBy);
    });
  });

  return groupedResults;
};

/** Prefix for sortBy when sorting by a metadata field. sortBy value is "metadata:FieldName". */
export const METADATA_SORT_PREFIX = "metadata:";

/**
 * Returns a comparable value for an item's metadata field (for sorting).
 * Returns empty string when item is a folder or has no value for the field.
 */
export function getMetadataCompareValue(item: Item, fieldName: string): string {
  if (item.type === ITEM_TYPE_FOLDER || !item.documentMetadataList) return "";
  const meta = item.documentMetadataList.find(
    (d) =>
      d.name === fieldName ||
      d.externalPropertyName?.toLowerCase() === fieldName.toLowerCase()
  );
  if (!meta?.metadata) return "";
  const m = meta.metadata;
  if (m.stringValue != null && m.stringValue !== "")
    return String(m.stringValue).trim();
  if (m.numberValue != null) return String(m.numberValue);
  if (m.booleanValue != null) return m.booleanValue ? "1" : "0";
  if (m.dateTimeValues?.length) {
    const first = m.dateTimeValues[0];
    return typeof first === "string"
      ? first
      : first instanceof Date
        ? first.toISOString()
        : String(first);
  }
  if (m.selectValues?.length) return m.selectValues.join(" ").trim();
  return "";
}

/**
 * Rank for sorting the Status (`isIndexed`) column: aligns with {@link getWorkspaceFileIndexingStatusPresentation}.
 * Uses the same active revision resolution as {@link FileList} / {@link FileGrid}.
 * Higher rank sorts first (indexed → neutral → running → stale → action-needed).
 */
const getWorkspaceFileIndexingStatusRankForSort = (
  item: Item,
  isIndexing: boolean
): number => {
  const isIndexed =
    item.revisions?.find((r) => r.revisionVersion === item.activeRevision)
      ?.isIndexed === true;
  const variant = getWorkspaceFileIndexingStatusPresentation({
    isIndexed,
    searchIndexStale: isActiveRevisionSearchIndexStale(item),
    documentType: item.documentType,
    isIndexing,
  }).variant;

  switch (variant) {
    case "indexed":
      return 4;
    case "notApplicable":
      return 3;
    case "indexing":
      // Ready soon without anyone acting, so ahead of the attention group.
      return 2;
    case "stale":
      return 1;
    case "actionNeeded":
      return 0;
    default: {
      const _exhaustive: never = variant;
      return _exhaustive;
    }
  }
};

/**
 * Compare two items for the given sort. `indexingIds` lists the documents with
 * a run in flight; only the Status sort reads it, and without it a running
 * document ranks by its stored state.
 */
export const sortItemsBy = (
  a: Item,
  b: Item,
  sortBy: string,
  indexingIds?: ReadonlySet<string>
) => {
  // folders first, then documents and links together
  if (a.type === ITEM_TYPE_FOLDER && b.type !== ITEM_TYPE_FOLDER) return -1;
  if (a.type !== ITEM_TYPE_FOLDER && b.type === ITEM_TYPE_FOLDER) return 1;

  if (sortBy.startsWith(METADATA_SORT_PREFIX)) {
    const fieldName = sortBy.slice(METADATA_SORT_PREFIX.length);
    const aVal = getMetadataCompareValue(a, fieldName);
    const bVal = getMetadataCompareValue(b, fieldName);
    return aVal.localeCompare(bVal, undefined, { sensitivity: "base" });
  }

  switch (sortBy) {
    case "name":
      return a.name.localeCompare(b.name);
    case "isIndexed": {
      if (a.type === ITEM_TYPE_FOLDER || b.type === ITEM_TYPE_FOLDER) return 0;
      const rankDelta =
        getWorkspaceFileIndexingStatusRankForSort(
          b,
          indexingIds?.has(b.id) ?? false
        ) -
        getWorkspaceFileIndexingStatusRankForSort(
          a,
          indexingIds?.has(a.id) ?? false
        );
      if (rankDelta !== 0) return rankDelta;
      return a.name.localeCompare(b.name);
    }
    case "date":
      return (
        new Date(b.updatedAt || "").getTime() -
        new Date(a.updatedAt || "").getTime()
      );
    case "size":
      if (a.type === ITEM_TYPE_FOLDER && b.type === ITEM_TYPE_FOLDER) {
        return (b.fileCount || 0) - (a.fileCount || 0);
      }
      if (a.type !== ITEM_TYPE_FOLDER && b.type !== ITEM_TYPE_FOLDER) {
        // Links have no size, so they sort after every document.
        return (b.size || 0) - (a.size || 0);
      }
      return 0;
    default:
      return 0;
  }
};
const workspaceFolderToItem = (
  folder: WorkspaceFolder,
  currentPath: { id: string; name: string }[] = [],
  isRoot: boolean = false,
  folderDateCache: WeakMap<WorkspaceFolder, string> = new WeakMap()
): Item => {
  const folderPath = isRoot
    ? undefined
    : buildFilePath(currentPath, folder.name);
  const newestDate = getNewestDateFromFolder(folder, folderDateCache);
  return {
    id: folder.id,
    blobName: "",
    name: folder.name,
    type: ITEM_TYPE_FOLDER,
    createdAt: folder.createdAt || "",
    updatedAt: newestDate,
    userId: folder.userId,
    fileCount:
      folder.documents.length +
      folder.folders.length +
      (folder.links?.length ?? 0),
    filePath: folderPath,
    driveId: folder.driveId,
    siteId: folder.siteId,
    sharepointFileId: folder.sharepointFileId,
    documentSource: folder.folderSource,
  };
};

const workspaceDocumentToItem = (
  doc: WorkspaceDocument,
  currentPath: { id: string; name: string }[] = [],
  isRoot: boolean = false
): Item => {
  const docPath = isRoot ? undefined : buildFilePath(currentPath, doc.name);

  return {
    id: doc.id,
    name: doc.name,
    type: ITEM_TYPE_DOCUMENT,
    size: doc.size,
    createdAt: doc.createdAt || "",
    updatedAt: doc.updatedAt || "",
    userId: doc.userId,
    documentType: doc.documentType,
    documentMetadataList: doc.documentMetadataList,
    documentSource: doc.documentSource,
    blobName: doc.blobName,
    driveId: doc.driveId,
    siteId: doc.siteId,
    sharepointFileId: doc.sharepointFileId,
    revisions: doc.revisions,
    activeRevision: doc.activeRevision,
    filePath: docPath,
  };
};

const workspaceLinkToItem = (
  link: WorkspaceLink,
  currentPath: { id: string; name: string }[] = [],
  isRoot: boolean = false
): Item => ({
  id: link.id,
  name: link.name,
  type: ITEM_TYPE_LINK,
  url: link.url,
  createdAt: link.createdAt || "",
  updatedAt: link.updatedAt || "",
  userId: link.userId,
  blobName: "",
  filePath: isRoot ? undefined : buildFilePath(currentPath, link.name),
});

// Helper function to recursively collect all documents from a folder
export function getAllDocumentsFromFolder(
  folder: WorkspaceFolder
): WorkspaceDocument[] {
  const documents: WorkspaceDocument[] = [...folder.documents];

  folder.folders.forEach((subfolder) => {
    documents.push(...getAllDocumentsFromFolder(subfolder));
  });

  return documents;
}

// Helper function to find folder by SharePoint ID
export function findFolderBySharePointId(
  folders: WorkspaceFolder[],
  sharepointFileId: string
): WorkspaceFolder | null {
  for (const folder of folders) {
    if (folder.sharepointFileId === sharepointFileId) {
      return folder;
    }
    const found = findFolderBySharePointId(folder.folders, sharepointFileId);
    if (found) {
      return found;
    }
  }
  return null;
}

// Helper function to recursively collect all folders with SharePoint metadata
export function getAllSharePointFolders(
  workspace: Workspace
): WorkspaceFolder[] {
  const folders: WorkspaceFolder[] = [];

  const collectFolders = (folderList: WorkspaceFolder[]) => {
    folderList.forEach((folder) => {
      if (folder.sharepointFileId && folder.driveId) {
        folders.push(folder);
      }
      collectFolders(folder.folders);
    });
  };

  collectFolders(workspace.folders);
  return folders;
}
